
import { SearchFlightRecommendationFilter, SearchFlightRecommendationFilterItem } from '@/types/models';
import { computed, defineComponent, PropType, ref } from 'vue';

import EasyDrop from '@/components/EasyDrop.vue';
import EasyMultiRangeSlider from '@/components/EasyMultiRangeSlider.vue';
import { useStore } from 'vuex';
import { LocaleStrings, MultiRangeValues, ValueIndexParams } from '@/types/paramModels';

export default defineComponent({
    name: "FlightFilter",
    emits: ["updateFilteredFlights"],
    components: {
        EasyDrop,
        EasyMultiRangeSlider
    },
    props: {
        filters: {
            type: Object as PropType<SearchFlightRecommendationFilter>,
            required: true
        },
        pageTexts: {
            type: Object,
            required: true
        },
        localeStrings: {
            type: Object as PropType<LocaleStrings>,
            required: true
        }
    },
    setup(_props, { emit }) {
        const store = useStore();
        const easyDrop = ref();

        const computedFilters = computed(() => _props.filters);

        const maxStopSelected = () => {
            return _props.filters.maxStops.find((stop) => { return stop.isSelected }) ?? { name: _props.pageTexts['hotelPageFlightStepFilterPopupLabelDropdownAll'] };
        }

        const resetMaxStops = () => {
            store.dispatch("TravelSearchBookingModule/resetFilterMaxStops").then(() => {
                emit("updateFilteredFlights");
            });
        }

        const isAllStopsSelected = computed(() => {
            return !computedFilters.value.maxStops.some(stop => stop.isSelected);
        })

        const selectedAirlinesCount = () => {
            return _props.filters.airlines.reduce((selectedCount, airline) => (airline.isSelected ? selectedCount + 1 : selectedCount), 0);
        }

        const selectedTravelClassCount = () => {
            return _props.filters.travelClass.reduce((selectedCount, travelClass) => (travelClass.isSelected ? selectedCount + 1 : selectedCount), 0);
        }

        const updateMaxStops = (stop: SearchFlightRecommendationFilterItem) => {
            store.dispatch('TravelSearchBookingModule/updateFilterMaxStops', stop).then(() => {
                emit("updateFilteredFlights");
            });
        }

        const updateAirline = (event: Event, index: number) => {
            const input = event.target as HTMLInputElement;
            const params = { value: input.checked, index: index } as ValueIndexParams;
            store.dispatch('TravelSearchBookingModule/updateFilterAirline', params).then(() => {
                emit("updateFilteredFlights");
            });
        }

        const isAllAirlinesSelected = computed(() => {
            return !computedFilters.value.airlines.some(airline => airline.isSelected);
        })

        const resetAirlines = () => {
            store.dispatch("TravelSearchBookingModule/resetFilterAirlines").then(() => {
                emit("updateFilteredFlights");
            });
        }

        const closeDropdown = () => {
            if (easyDrop.value)
                easyDrop.value.toggleDropdown();
        }

        const updateTravelClass = (event: Event, index: number) => {
            const input = event.target as HTMLInputElement;
            const params = { value: input.checked, index: index } as ValueIndexParams;
            store.dispatch('TravelSearchBookingModule/updateFilterTravelClass', params).then(() => {
                emit("updateFilteredFlights");
            });
        }

        const isAllTravelClassesSelected = computed(() => {
            return !computedFilters.value.travelClass.some(travelClass => travelClass.isSelected);
        })

        const resetTravelClass = (event: Event) => {
            const input = event.target as HTMLInputElement;
            if (!input.checked) {
                event.preventDefault();
            } else {
                store.dispatch("TravelSearchBookingModule/resetFilterTravelClasses").then(() => {
                    emit("updateFilteredFlights");
                });
            }
        }

        const updateMinTravelLength = (inputValue: number) => {
            store.dispatch('TravelSearchBookingModule/updateFilterMinTravelLength', inputValue).then(() => {
                emit("updateFilteredFlights");
            });
        }

        const updateMaxTravelLength = (inputValue: number) => {
            store.dispatch('TravelSearchBookingModule/updateFilterMaxTravelLength', inputValue).then(() => {
                emit("updateFilteredFlights");
            });
        }

        const UpdateTravelLength = (values: MultiRangeValues) => {
            store.dispatch('TravelSearchBookingModule/updateFilterTravelLength', values).then(() => {
                emit("updateFilteredFlights");
            });
        }

        const resetAllFilters = () => {
            store.dispatch('TravelSearchBookingModule/resetFilterAll').then(() => {
                emit("updateFilteredFlights");
            });
        }

        return {
            maxStopSelected,
            resetMaxStops,
            selectedAirlinesCount,
            selectedTravelClassCount,
            updateMaxStops,
            updateAirline,
            updateTravelClass,
            UpdateTravelLength,
            updateMinTravelLength,
            updateMaxTravelLength,
            isAllStopsSelected,
            easyDrop,
            closeDropdown,
            computedFilters,
            isAllAirlinesSelected,
            resetAirlines,
            isAllTravelClassesSelected,
            resetTravelClass,
            resetAllFilters
        }
    },
})
